import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePsychologistContext } from '../../contexts/PsychologistContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNzJourneyStatus } from '../../hooks/useNzJourneyStatus';
import { getPsychologistMetrics, PsychologistMetrics } from '../../hooks/api/psychologist/metrics';
import { metricsPath } from '../../routes/slugs';
import CircularProgress from '../../components/common/CircularProgress';
import { MetricsCaminoInicialTab } from '../../components/psychologist/profile/metrics/MetricsCaminoInicialTab';
import { JourneyAcceptedArticlesPanel } from '../../components/psychologist/profile/metrics/JourneyAcceptedArticlesPanel';

/**
 * Tab de nivel superior "Camino Inicial" — solo NZ activa, en transición (O viniendo de NZ,
 * pending-discharge) o recién graduada sin confirmar egreso (ver `useNzJourneyStatus.journeyMode`,
 * gate y visibilidad del tab en `AppShell`). Antes vivía como sub-tab dentro de
 * Perfil → Métricas (`ProfileMetricsTab`); se mudó acá para que tenga su propio espacio de
 * nivel superior, con dos columnas: a la izquierda los artículos ya desbloqueados/aceptados,
 * a la derecha el flujo de Camino Inicial en sí (sin cambios de lógica, mismo componente).
 */
export const CaminoInicialPage: React.FC = () => {
  const navigate = useNavigate();
  const { psychologist } = usePsychologistContext();
  const { psychologistId } = useAuth();
  const resolvedPsychologistId = psychologistId || psychologist?.id || '';
  const { journeyModeLoading, isDischargeStatus, isGraduated } = useNzJourneyStatus();

  const [metrics, setMetrics] = useState<PsychologistMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMetricsLoading(true);
    setMetricsError(false);
    getPsychologistMetrics()
      .then((res) => { if (!cancelled) setMetrics(res); })
      .catch(() => {
        if (cancelled) return;
        setMetrics(null);
        setMetricsError(true);
      })
      .finally(() => { if (!cancelled) setMetricsLoading(false); });
    return () => { cancelled = true; };
  }, [resolvedPsychologistId]);

  if (journeyModeLoading || !resolvedPsychologistId) {
    return (
      <div className="flex justify-center py-16">
        <CircularProgress label="Cargando tu camino inicial..." />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 pt-8 pb-16">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-6">
        <div className="md:w-1/3">
          <JourneyAcceptedArticlesPanel psychologistId={resolvedPsychologistId} />
        </div>
        <div className="md:w-2/3">
          <MetricsCaminoInicialTab
            metrics={metrics}
            loading={metricsLoading}
            metricsError={metricsError}
            psychologistId={resolvedPsychologistId}
            isDischargeStatus={isDischargeStatus}
            isGraduated={isGraduated}
            onGoToLiquidacion={() => navigate(metricsPath('liquidacion'))}
          />
        </div>
      </div>
    </div>
  );
};

export default CaminoInicialPage;
